import React, { useRef } from 'react'
import {
  Checkbox,
  Col,
  Form,
  List,
  Modal,
  Radio,
  Row,
  Tooltip,
  Typography
} from 'antd'
import { Formik, FormikProps } from 'formik'
import { useTranslation } from 'react-i18next'

import { Button } from 'src/components/Ant'
import {
  currencyMask,
  InputCurrency,
  Select,
  SelectResource
} from 'src/components/Formik'
import {
  Currency,
  Policy,
  PolicyAction,
  PolicyExpenseLimits,
  Reason,
  Reference,
  Service,
  ServiceType
} from 'src/types'

import * as CSS from './styles'
import * as Values from './initial-values'

import { Empty, PolicyRuleAlertIcon, tOperator } from '../utils'

type RecordListProps = {
  record: Currency | Reason | Reference | Service
}

function RecordText({ text }: { text: string }) {
  if (text?.length > 25) {
    return (
      <Tooltip title={text}>
        <Typography.Text>{`${text.substring(0, 17)}...`}</Typography.Text>
      </Tooltip>
    )
  }
  return <Typography.Text ellipsis>{text}</Typography.Text>
}

function RecordList({ record }: RecordListProps): JSX.Element {
  const text =
    record.code && record.name
      ? `${record.code} - ${record.name}`
      : record.code || record.name

  return <RecordText text={text} />
}

export default function PolicyLimits({
  formikProps
}: {
  formikProps: FormikProps<Policy>
}) {
  const refFormik = useRef(null)
  const { t } = useTranslation()

  const { policy_id } = formikProps.values

  const initialLimit = Values.getInitialValuesPolicyExpenseLimits({ policy_id })

  function add() {
    refFormik.current?.resetForm(initialLimit)
  }

  const operators = [
    {
      label: t('common.condition.less-than'),
      value: '<'
    },
    {
      label: t('common.condition.less-than-or-equal'),
      value: '<='
    },
    {
      label: t('common.condition.equal'),
      value: '=='
    },
    {
      label: t('common.condition.greater-than'),
      value: '>'
    },
    {
      label: t('common.condition.greater-than-or-equal'),
      value: '>='
    }
  ]

  function handleSubmit(values: PolicyExpenseLimits) {
    const foundIndex = formikProps.values.policy_expense_limits.findIndex(
      PoExLi =>
        PoExLi.policy_expense_limit_id === values.policy_expense_limit_id
    )

    if (foundIndex > -1) {
      const policy_expense_limits = [
        ...formikProps.values.policy_expense_limits
      ]

      policy_expense_limits[foundIndex] = values
      formikProps.setFieldValue(`policy_expense_limits`, policy_expense_limits)
    } else {
      //is new
      formikProps.setFieldValue(`policy_expense_limits`, [
        ...formikProps.values.policy_expense_limits,
        values
      ])
    }

    refFormik.current?.resetForm(Values.initialValues)
  }

  const tAlert = t('web.policies.action.alert')
  const tBlock = t('web.policies.action.block')

  return (
    <>
      <CSS.Title level={3}>{t('web.policies.limits.title')}</CSS.Title>
      <CSS.LimitContainer>
        <CSS.List
          bordered
          dataSource={formikProps.values.policy_expense_limits}
          locale={{
            emptyText: <Empty message={t('common.no-data-found')} />
          }}
          header={
            <Row>
              <Col span={4}>
                <Typography.Text strong>
                  {t('web.policies.limits.service')}
                </Typography.Text>
              </Col>
              <Col span={3}>
                <Typography.Text strong>
                  {t('web.policies.limits.reason')}
                </Typography.Text>
              </Col>
              <Col span={4}>
                <Typography.Text strong>
                  {t('web.policies.limits.reference')}
                </Typography.Text>
              </Col>
              <Col span={3}>
                <Typography.Text strong>
                  {t('web.policies.limits.currency')}
                </Typography.Text>
              </Col>
              <Col span={2}>
                <Typography.Text strong>
                  {t('common.condition.title')}
                </Typography.Text>
              </Col>
              <CSS.Col span={2}>
                <Typography.Text strong>
                  {t('web.policies.rules.value')}
                </Typography.Text>
              </CSS.Col>
              <CSS.Col span={2}>
                <Typography.Text strong>
                  {t('web.policies.limits.limit')}
                </Typography.Text>
              </CSS.Col>
              <CSS.Col span={2}>
                <Typography.Text strong>
                  {t('web.policies.limits.daily')}
                </Typography.Text>
              </CSS.Col>
              <CSS.Col span={1}>
                <Typography.Text strong>
                  {t('web.policies.action.title')}
                </Typography.Text>
              </CSS.Col>
              <CSS.Col span={1}>
                <Typography.Text strong>{t('common.active')}</Typography.Text>
              </CSS.Col>
            </Row>
          }
          renderItem={(policyExpenseLimit, itemIndex) => {
            const isServiceTypeValue =
              ServiceType.QUANTITY !== policyExpenseLimit.service?.service_type

            return (
              <List.Item
                key={policyExpenseLimit.policy_expense_limit_id}
                onClick={() => {
                  refFormik.current?.resetForm(policyExpenseLimit)
                }}
              >
                <Col span={4}>
                  <RecordList record={policyExpenseLimit.service} />
                </Col>
                <Col span={3}>
                  <RecordList record={policyExpenseLimit.reason} />
                </Col>
                <Col span={4}>
                  <RecordList record={policyExpenseLimit.reference} />
                </Col>
                <Col span={3}>
                  <RecordList record={policyExpenseLimit.currency} />
                </Col>
                <Col span={2}>{t(tOperator(policyExpenseLimit.operator))}</Col>
                <CSS.Col span={2}>
                  {!isServiceTypeValue &&
                    currencyMask({
                      formatStr: true,
                      precision: 2,
                      value: policyExpenseLimit.value
                    }).maskedValue}
                </CSS.Col>
                <CSS.Col span={2}>
                  {
                    currencyMask({
                      formatStr: true,
                      precision: 2,
                      value: policyExpenseLimit.limit || 0
                    }).maskedValue
                  }
                </CSS.Col>
                <CSS.Col span={2}>
                  {
                    currencyMask({
                      formatStr: true,
                      precision: 2,
                      value: policyExpenseLimit.daily_limit || 0
                    }).maskedValue
                  }
                </CSS.Col>
                <CSS.Col span={1}>
                  <PolicyRuleAlertIcon
                    action={policyExpenseLimit.action}
                    alert={tAlert}
                    block={tBlock}
                  />
                </CSS.Col>
                <CSS.Col span={1} onClick={event => event.stopPropagation()}>
                  <Checkbox
                    defaultChecked={Boolean(policyExpenseLimit?.active)}
                    onChange={value => {
                      const { checked } = value.target
                      formikProps.setFieldValue(
                        `policy_expense_limits[${itemIndex}].active`,
                        checked
                      )
                    }}
                  />
                </CSS.Col>
              </List.Item>
            )
          }}
        />
      </CSS.LimitContainer>
      <CSS.ContainerButtonAdd>
        <Button
          icon="plus"
          key="common.actions.add"
          onClick={add}
          size="large"
          type="primary"
        >
          {t('common.actions.add')}
        </Button>
      </CSS.ContainerButtonAdd>
      <Formik
        initialValues={null}
        onSubmit={handleSubmit}
        ref={refFormik}
        render={(editLimit: FormikProps<PolicyExpenseLimits>) => {
          const isEditing = Boolean(editLimit?.values?.policy_expense_limit_id)
          const title = t(
            isEditing
              ? 'web.policies.limits.modal-title-edit'
              : 'web.policies.limits.modal-title-new'
          )

          return (
            <Modal
              centered
              closable
              destroyOnClose
              onCancel={() => {
                refFormik.current?.resetForm(Values.initialValues)
              }}
              onOk={() => {
                refFormik.current?.handleSubmit()
              }}
              title={title}
              visible={isEditing}
              width={700}
            >
              <Row align="middle" gutter={24} justify="space-between">
                <Col span={12}>
                  <SelectResource
                    allowAll
                    defaultActiveFirstOption
                    formikProps={editLimit}
                    keyLabel="name"
                    keyValue="reason_id"
                    label="web.resources.reasons.title"
                    name="reason_id"
                    resource="reasons"
                    style={{ width: '100%' }}
                  />
                </Col>
                <Col span={12}>
                  <SelectResource
                    allowAll
                    formikProps={editLimit}
                    keyLabel="name"
                    keyValue="reference_id"
                    label="web.resources.references.title"
                    name="reference_id"
                    resource="references"
                    style={{ width: '100%' }}
                  />
                </Col>
              </Row>
              <Row gutter={24}>
                <Col span={16}>
                  <SelectResource
                    allowAll
                    formikProps={editLimit}
                    keyLabel="name"
                    keyValue="service_id"
                    label="web.receipt.form.service.title"
                    name="service_id"
                    resource="services"
                    style={{ width: '100%' }}
                  />
                </Col>
                <Col span={8}>
                  <SelectResource
                    allowAll
                    formikProps={editLimit}
                    keyLabel="code"
                    keyValue="currency_id"
                    label="web.resources.currencies.title"
                    name="currency_id"
                    resource="currencies"
                    style={{ width: '100%' }}
                  />
                </Col>
              </Row>
              <Row gutter={24}>
                <Col span={6}>
                  <Select
                    dataTestId="operator-list"
                    formikProps={editLimit}
                    label="common.condition.title"
                    name="operator"
                    options={operators}
                    style={{ width: '100%' }}
                  />
                </Col>
                <Col span={6}>
                  <InputCurrency
                    dataTestId="policy-amount"
                    disabled={
                      editLimit.values?.service?.service_type !==
                      ServiceType.QUANTITY
                    }
                    formikProps={editLimit}
                    label="web.policies.limits.value-info"
                    name="value"
                    required={
                      editLimit.values?.service?.service_type ===
                      ServiceType.QUANTITY
                    }
                  />
                </Col>
                <Col span={6}>
                  <InputCurrency
                    dataTestId="policy-limit"
                    formikProps={editLimit}
                    label="web.policies.limits.limit"
                    name="limit"
                  />
                </Col>
                <Col span={6}>
                  <InputCurrency
                    dataTestId="policy-daily-limit"
                    formikProps={editLimit}
                    label="web.policies.limits.daily"
                    name="daily_limit"
                  />
                </Col>
              </Row>
              <Row gutter={24}>
                <Col span={6}>
                  <Form.Item
                    colon={false}
                    label={t('web.policies.action.title')}
                  >
                    <Radio.Group
                      buttonStyle="solid"
                      onChange={e => {
                        let policyAction: PolicyAction = PolicyAction.ALERT

                        if (e.target.value === 'BLOCK') {
                          policyAction = PolicyAction.BLOCK
                        }

                        editLimit.setFieldValue('action', policyAction)
                      }}
                      size="small"
                      value={editLimit.values?.action}
                    >
                      <Radio.Button value="ALERT">{tAlert}</Radio.Button>
                      <Radio.Button value="BLOCK">{tBlock}</Radio.Button>
                    </Radio.Group>
                  </Form.Item>
                </Col>
              </Row>
            </Modal>
          )
        }}
      />
    </>
  )
}
